import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { MudFormulationService } from 'src/app/services/mud-formulation.service';
import { ChemicalsService } from 'src/app/services/chemicals.service';
import { ChemicalListItemDto } from '@models/chemical-list-item-dto.interface';
import {
  MudAdditiveResultDto,
  MudCalculationResultDto,
  SaveMudFormulationDto,
} from '@models/mud-formulation-dto.interface';

@Component({
  selector: 'app-mud-formulation',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, MatIconModule, MatAutocompleteModule],
  templateUrl: './mud-formulation.component.html',
  styleUrl: './mud-formulation.component.scss',
})
export class MudFormulationComponent implements OnInit, OnChanges {
  @Input() jobRequestId: number | null = null;
  @Input() isEditable = false;
  @Input() brineDensityPpg: number | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() brineVolumeMlChanged = new EventEmitter<number | null>();

  private readonly mudFormulationService = inject(MudFormulationService);
  private readonly chemicalsService = inject(ChemicalsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected chemicals = signal<ChemicalListItemDto[]>([]);
  protected result = signal<MudCalculationResultDto | null>(null);
  protected isLoading = signal(false);
  protected isCalculating = signal(false);
  protected isSaving = signal(false);
  protected errorMessage = signal('');

  protected form = this.fb.group({
    mudWeightPpg: [null as number | null, Validators.required],
    oilPercent: [null as number | null, Validators.required],
    waterPercent: [null as number | null, Validators.required],
    baseOilDensityPpg: [null as number | null, Validators.required],
    weightMaterialDensityPpg: [null as number | null, Validators.required],
    additives: this.fb.array<FormGroup>([]),
  });

  protected focusNextInput(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;

    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  get additivesArray(): FormArray<FormGroup> {
    return this.form.get('additives') as FormArray<FormGroup>;
  }

  ngOnInit(): void {
    this.chemicalsService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(list => this.chemicals.set(list));

    this.form.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recalculate());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobRequestId'] && this.jobRequestId) {
      this.loadExisting(this.jobRequestId);
    }

    if (changes['brineDensityPpg'] && !changes['brineDensityPpg'].isFirstChange()) {
      this.recalculate();
    }
  }

  protected onAddMaterial(): void {
    this.additivesArray.push(this.buildAdditiveGroup());
  }

  protected onRemoveMaterial(index: number): void {
    this.additivesArray.removeAt(index);
  }

  protected filteredChemicals(value: string | ChemicalListItemDto | null): ChemicalListItemDto[] {
    const search = typeof value === 'string' ? value.toLowerCase().trim() : '';
    if (!search) return this.chemicals();
    return this.chemicals().filter(chemical => chemical.name.toLowerCase().includes(search));
  }

  protected displayChemical(chemical: ChemicalListItemDto | string | null): string {
    return typeof chemical === 'string' ? chemical : chemical?.name ?? '';
  }

  protected onChemicalSelected(index: number, event: MatAutocompleteSelectedEvent): void {
    const group = this.additivesArray.at(index);
    const chemical = event.option.value as ChemicalListItemDto;
    group.patchValue({
      chemicalId: chemical.id,
      name: chemical?.name ?? null,
      specificGravity: chemical?.specificGravity ?? null,
    });
  }

  protected additiveResult(index: number): MudAdditiveResultDto | null {
    return this.result()?.additives?.[index] ?? null;
  }

  protected onSave(): void {
    if (!this.jobRequestId || !this.result() || this.isSaving()) return;

    const dto = this.buildSaveDto();
    this.isSaving.set(true);
    this.errorMessage.set('');

    this.mudFormulationService.save(this.jobRequestId, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: saved => {
          this.isSaving.set(false);
          this.result.set(saved.calculation);
          this.brineVolumeMlChanged.emit(saved.calculation.brineMilliliters);
          this.changed.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          this.errorMessage.set(this.extractErrorMessage(err));
        },
      });
  }

  private loadExisting(jobRequestId: number): void {
    this.isLoading.set(true);
    this.mudFormulationService.getByJobRequest(jobRequestId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dto => {
          this.isLoading.set(false);
          this.result.set(dto.calculation);
          this.brineVolumeMlChanged.emit(dto.calculation.brineMilliliters);
          this.additivesArray.clear({ emitEvent: false });
          for (const additive of dto.calculation.additives) {
            this.additivesArray.push(this.buildAdditiveGroup(additive), { emitEvent: false });
          }
          this.form.patchValue({
            mudWeightPpg: dto.mudWeightPpg,
            oilPercent: dto.oilPercent,
            waterPercent: dto.waterPercent,
            baseOilDensityPpg: dto.baseOilDensityPpg,
            weightMaterialDensityPpg: dto.weightMaterialDensityPpg,
          }, { emitEvent: false });
        },
        error: () => {
          // No saved mud formulation yet for this job request.
          this.isLoading.set(false);
        },
      });
  }

  private recalculate(): void {
    const raw = this.form.getRawValue();
    if (raw.mudWeightPpg === null || raw.oilPercent === null || raw.waterPercent === null
      || raw.baseOilDensityPpg === null || raw.weightMaterialDensityPpg === null
      || this.brineDensityPpg === null) {
      return;
    }

    this.isCalculating.set(true);
    this.errorMessage.set('');

    this.mudFormulationService.calculate({
      mudWeightPpg: raw.mudWeightPpg,
      oilPercent: raw.oilPercent,
      waterPercent: raw.waterPercent,
      baseOilDensityPpg: raw.baseOilDensityPpg,
      weightMaterialDensityPpg: raw.weightMaterialDensityPpg,
      brineDensityPpg: this.brineDensityPpg,
      additives: raw.additives.map((a: any, i: number) => ({
        id: a.id,
        chemicalId: a.chemicalId,
        name: a.name,
        sequenceNumber: i,
        concentration: a.concentration,
        specificGravity: a.specificGravity,
      })),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: calc => {
        this.isCalculating.set(false);
        this.result.set(calc);
        this.brineVolumeMlChanged.emit(calc.brineMilliliters);
      },
      error: (err: HttpErrorResponse) => {
        this.isCalculating.set(false);
        this.result.set(null);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private buildSaveDto(): SaveMudFormulationDto {
    const raw = this.form.getRawValue();
    return {
      mudWeightPpg: raw.mudWeightPpg,
      oilPercent: raw.oilPercent,
      waterPercent: raw.waterPercent,
      baseOilDensityPpg: raw.baseOilDensityPpg,
      weightMaterialDensityPpg: raw.weightMaterialDensityPpg,
      brineDensityPpg: this.brineDensityPpg,
      additives: raw.additives.map((a: any, i: number) => ({
        id: a.id,
        chemicalId: a.chemicalId,
        name: a.name,
        sequenceNumber: i,
        concentration: a.concentration,
        specificGravity: a.specificGravity,
      })),
    };
  }

  private buildAdditiveGroup(additive?: MudAdditiveResultDto): FormGroup {
    return this.fb.group({
      id: [additive?.id ?? null],
      chemicalId: [additive?.chemicalId ?? null, Validators.required],
      name: [additive?.name ?? null],
      materialSearch: [additive?.name ?? ''],
      concentration: [additive?.concentration ?? null, Validators.required],
      specificGravity: [additive?.specificGravity ?? null],
    });
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const errors = err.error?.errors as Record<string, string[]> | undefined;
    if (errors) {
      const firstMessage = Object.values(errors)[0]?.[0];
      if (firstMessage) return firstMessage;
    }
    return 'Unable to calculate mud formulation. Please check your entries.';
  }
}
